import { Action, createReducer, on } from '@ngrx/store';
import { ProvisioningActions } from '../actions';


export const provisioningFeatureKey = 'provisioning';

export interface State {
  provisioningEnabled: boolean;
  provisioningStatusLoading: boolean;
  provisioningStatusLoaded: boolean;
  provisioningStatusError: any;

  switchProvisioningStatusProcessing: boolean;
  switchProvisioningStatusSuccess: boolean;
  switchProvisioningStatusError: any;

  generatedSCIMToken: string;
  generateSCIMTokenProcessing: boolean;
  generateSCIMTokenSuccess: boolean;
  generateSCIMTokenError: any;

  resetProvisioningProcessing: boolean;
  resetProvisioningSuccess: boolean;
  resetProvisioningError: any;



  disabledUsersToDeleteCount?: number;
  disabledUsersToDeleteProcessing: boolean;
  disabledUsersToDeleteSuccess: boolean;
  disabledUsersToDeleteError: any;

  deletedUserCount?: number;
  deleteDisabledUsersProcessing: boolean;
  deleteDisabledUsersSuccess: boolean;
  deleteDisabledUsersError: any;
}

export const initialState: State = {
  provisioningEnabled: false,
  provisioningStatusLoading: false,
  provisioningStatusLoaded: false,
  provisioningStatusError: null,

  switchProvisioningStatusProcessing: false,
  switchProvisioningStatusSuccess: false,
  switchProvisioningStatusError: null,

  generatedSCIMToken: null,
  generateSCIMTokenProcessing: false,
  generateSCIMTokenSuccess: false,
  generateSCIMTokenError: null,

  resetProvisioningProcessing: false,
  resetProvisioningSuccess: false,
  resetProvisioningError: null,

  disabledUsersToDeleteCount: undefined,
  disabledUsersToDeleteProcessing: false,
  disabledUsersToDeleteSuccess: false,
  disabledUsersToDeleteError: null,

  deletedUserCount: undefined,
  deleteDisabledUsersProcessing: false,
  deleteDisabledUsersSuccess: false,
  deleteDisabledUsersError: null,
};

const provisioningReducer = createReducer(
  initialState,
  // Load Provisioning Status
  on(ProvisioningActions.LoadProvisioningStatus, (state) => ({
    ...state,
    provisioningEnabled: initialState.provisioningEnabled,
    provisioningStatusLoading: true,
    provisioningStatusLoaded: initialState.provisioningStatusLoaded,
    provisioningStatusError: initialState.provisioningStatusError,
  })),
  on(ProvisioningActions.LoadProvisioningStatusSuccess, (state, { active }) => ({
    ...state,
    provisioningEnabled: active,
    provisioningStatusLoading: false,
    provisioningStatusLoaded: true,
  })),
  on(ProvisioningActions.LoadProvisioningStatusFailure, (state, { error }) => ({
    ...state,
    provisioningStatusLoading: false,
    provisioningStatusError: error
  })),
  // Switch Provisioning Status
  on(ProvisioningActions.SwitchProvisioningStatus, (state) => ({
    ...state,
    switchProvisioningStatusProcessing: true,
    switchProvisioningStatusSuccess: initialState.switchProvisioningStatusSuccess,
    switchProvisioningStatusError: initialState.switchProvisioningStatusError,
  })),
  on(ProvisioningActions.SwitchProvisioningStatusSuccess, (state, { active }) => ({
    ...state,
    provisioningEnabled: active,
    switchProvisioningStatusSuccess: true,
    switchProvisioningStatusProcessing: false,
  })),
  on(ProvisioningActions.SwitchProvisioningStatusFailure, (state, { error }) => ({
    ...state,
    switchProvisioningStatusProcessing: false,
    switchProvisioningStatusError: error
  })),
  // Generate SCIM Token
  on(ProvisioningActions.GenerateSCIMToken, (state) => ({
    ...state,
    generateSCIMTokenProcessing: true,
    generateSCIMTokenSuccess: initialState.generateSCIMTokenSuccess,
    generateSCIMTokenError: initialState.generateSCIMTokenError,
  })),
  on(ProvisioningActions.GenerateSCIMTokenSuccess, (state, { token }) => ({
    ...state,
    generatedSCIMToken: token,
    generateSCIMTokenSuccess: true,
    generateSCIMTokenProcessing: false,
  })),
  on(ProvisioningActions.GenerateSCIMTokenFailure, (state, { error }) => ({
    ...state,
    generateSCIMTokenProcessing: false,
    generateSCIMTokenError: error
  })),
  // Reset Provisioning
  on(ProvisioningActions.ResetProvisioning, (state) => ({
    ...state,
    resetProvisioningProcessing: true,
    resetProvisioningSuccess: initialState.resetProvisioningSuccess,
    resetProvisioningError: initialState.resetProvisioningError,
  })),
  on(ProvisioningActions.ResetProvisioningSuccess, (state) => ({
    ...state,
    resetProvisioningSuccess: true,
    resetProvisioningProcessing: false,
  })),
  on(ProvisioningActions.ResetProvisioningFailure, (state, { error }) => ({
    ...state,
    resetProvisioningProcessing: false,
    resetProvisioningError: error
  })),
  // Delete Disabled Users
  on(ProvisioningActions.ResetDeleteDisabledUsers, (state) => ({
    ...state,
    deletedUserCount: undefined,
    deleteDisabledUsersProcessing: initialState.deleteDisabledUsersProcessing,
    deleteDisabledUsersSuccess: initialState.deleteDisabledUsersSuccess,
    deleteDisabledUsersError: initialState.deleteDisabledUsersError,
  })),
  on(ProvisioningActions.DeleteDisabledUsers, (state) => ({
    ...state,
    deleteDisabledUsersProcessing: true,
    deleteDisabledUsersSuccess: initialState.deleteDisabledUsersSuccess,
    deleteDisabledUsersError: initialState.deleteDisabledUsersError,
  })),
  on(ProvisioningActions.DeleteDisabledUsersSuccess, (state, { deletedUserCount }) => ({
    ...state,
    deletedUserCount: deletedUserCount,
    deleteDisabledUsersSuccess: true,
    deleteDisabledUsersProcessing: false,
  })),
  on(ProvisioningActions.DeleteDisabledUsersFailure, (state, { error, code }) => ({
    ...state,
    deleteDisabledUsersProcessing: false,
    deleteDisabledUsersError: code
  })),
  on(ProvisioningActions.ListDisabledUsersToDelete, (state) => ({
    ...state,
    disabledUsersToDeleteProcessing: true,
    disabledUsersToDeleteSuccess: initialState.disabledUsersToDeleteSuccess,
    disabledUsersToDeleteError: initialState.disabledUsersToDeleteError,
  })),
  on(ProvisioningActions.ListDisabledUsersToDeleteSuccess, (state, { disabledUsersToDelete }) => ({
    ...state,
    disabledUsersToDeleteCount: disabledUsersToDelete,
    disabledUsersToDeleteSuccess: true,
    disabledUsersToDeleteProcessing: false,
  })),
  on(ProvisioningActions.ListDisabledUsersToDeleteFailure, (state, { error }) => ({
    ...state,
    disabledUsersToDeleteProcessing: false,
    disabledUsersToDeleteError: error
  })),
);

export function reducer(state: State | undefined, action: Action) {
  return provisioningReducer(state, action);
}
